import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Label,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Container,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import PurchaseService from "../../../services/OrderingServices/PurchaseService";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import ItemService from "../../../services/OrderingServices/ItemService";
import SupplierService from "../../../services/OrderingServices/SupplierService";
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";

// Validation Schema
const validationSchema = Yup.object({
  purchaseDate: Yup.date().required("Purchase date and time is required"),
  supplierId: Yup.number().required("Supplier is required"),
  items: Yup.array()
    .of(
      Yup.object({
        productId: Yup.number().required("Product is required"),
        productName: Yup.string().required("Product name is required"),
        quantity: Yup.number()
          .min(1, "Quantity must be at least 1")
          .required("Required"),
        unitPrice: Yup.number()
          .min(0, "Price cannot be negative")
          .required("Required"),
      })
    )
    .min(1, "At least one item is required"),
  voucherNo: Yup.string().required("Voucher number is required"),
  payment: Yup.object({
    amountPaid: Yup.number()
      .min(0, "Amount cannot be negative")
      .test(
        "amountPaid-max",
        "Amount paid cannot exceed the grand total",
        function (value) {
          const items = this.from[1].value.items || [];
          const total = items.reduce((sum, item) => {
            return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
          }, 0);
          return (parseFloat(value) || 0) <= total;
        }
      ),
  }),
});

const PurchaseFormPage = () => {
  const { id } = useParams();
  const [orgDetail, setOrgDetail] = useState(null);
  const [productData, setProductData] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [paymentTypeData, setPaymentTypeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const flatpickrRef = useRef(null);
  const breadcrumbItems = [{ title: "Back To List", link: "/purchase" }];

  // Fetch purchase details
  useEffect(() => {
    const fetchOrgDetail = async () => {
      if (id) {
        try {
          setLoading(true);
          const response = await PurchaseService.detail(id);
          setOrgDetail(response);
        } catch (error) {
          console.error("Error fetching purchase details:", error);
          toast.error("Failed to load purchase details");
        } finally {
          setLoading(false);
        }
      }
    };
    fetchOrgDetail();
  }, [id]);

  // Fetch products
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await ItemService.itemList();
        setProductData(response);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products");
      }
    };
    fetchProduct();
  }, []);

  // Fetch suppliers
  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        const response = await SupplierService.getList(1, 100);
        setSupplierList(response);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        toast.error("Failed to load suppliers");
      }
    };
    fetchSupplier();
  }, []);

  // Fetch payment types
  useEffect(() => {
    const fetchPaymentTypes = async () => {
      try {
        const response = await PaymentTypeServices.getList();
        setPaymentTypeData(response);
      } catch (error) {
        console.error("Error fetching payment types:", error);
      }
    };
    fetchPaymentTypes();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      purchaseDate: orgDetail?.purchaseDate || "",
      supplierId: orgDetail?.supplierId || "",
      voucherNo: orgDetail?.voucherNo || orgDetail?.refrenceNo || "",
      items: orgDetail?.items || [
        {
          productId: "",
          productName: "",
          quantity: "",
          unitPrice: "",
        },
      ],
      payment: {
        paymentMethodId: orgDetail?.paymentMethodId || "",
        amountPaid: orgDetail?.amountPaid || "",
        paymentRemarks: orgDetail?.paymentRemarks || "",
      },
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        const payload = {
          id: id ? parseInt(id) : undefined,
          purchaseDate: new Date(values.purchaseDate).toISOString(),
          supplierId: parseInt(values.supplierId) || 0,
          voucherNo: values.voucherNo,
          items: values.items
            .filter((item) => item.productId && item.productId !== "")
            .map((item) => ({
              productId: parseInt(item.productId) || 0,
              productName: item.productName || "",
              quantity: parseInt(item.quantity) || 0,
              unitPrice: parseFloat(item.unitPrice) || 0,
            })),
        };

        // Only include payment if a payment method is specifically selected
        if (values.payment.paymentMethodId && values.payment.paymentMethodId !== "") {
          payload.payment = {
            paymentMethodId: parseInt(values.payment.paymentMethodId) || 0,
            amountPaid: parseFloat(values.payment.amountPaid) || 0,
            paymentRemarks: values.payment.paymentRemarks || "",
          };
        }

        if (id) {
          await PurchaseService.update(id, payload);
          toast.success("Purchase updated successfully");
        } else {
          await PurchaseService.create(payload);
          toast.success("Purchase created successfully");
          resetForm();
        }
        navigate("/purchase");
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(
          error?.response?.data?.message || "Failed to save purchase"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const productOptions =
    productData?.items?.map((type) => ({
      value: type.id,
      label: type.name,
      price: type.price,
    })) || [];

  const supplierOptions =
    supplierList?.map((supplier) => ({
      value: supplier.id,
      label: supplier.name,
    })) || [];

  const handleAddItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      {
        productId: "",
        productName: "",
        quantity: "",
        unitPrice: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue("items", newItems);
  };

  const handleProductChange = (index, selectedOption) => {
    formik.setFieldValue(
      `items[${index}].productId`,
      selectedOption ? selectedOption.value : ""
    );
    formik.setFieldValue(
      `items[${index}].productName`,
      selectedOption ? selectedOption.label : ""
    );
    formik.setFieldValue(
      `items[${index}].unitPrice`,
      selectedOption ? selectedOption.price || "" : ""
    );
  };

  const handleCancel = () => {
    navigate("/purchase");
  };

  const calculateTotal = (quantity, unitPrice) => {
    const qty = parseFloat(quantity) || 0;
    const price = parseFloat(unitPrice) || 0;
    return qty * price;
  };

  const calculateGrandTotal = () => {
    return formik.values.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      return sum + qty * price;
    }, 0);
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,.15)" : "none",
      "&:hover": { borderColor: "#86b7fe" },
      fontSize: "0.875rem",
    }),
    option: (base) => ({ ...base, fontSize: "0.875rem" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const sectionStyle = {
    background: "#f8f9fc",
    borderRadius: "10px",
    padding: "20px 20px 4px",
    marginBottom: "20px",
    border: "1px solid #eef0f7",
  };

  const sectionHeaderStyle = {
    fontSize: "0.75rem",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "#6c757d",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  if (loading && id) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  const grandTotal = calculateGrandTotal();

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          pageTitle="Purchase"
          title={id ? "Update Purchase" : "Add Purchase"}
          breadcrumbItems={breadcrumbItems}
        />

        <Form onSubmit={formik.handleSubmit}>
          <Row className="g-4 align-items-start">
            {/* ─── LEFT: Form Sections ─── */}
            <Col lg={8}>
              <Card className="border-0" style={{ borderRadius: "14px", boxShadow: "0 2px 16px rgba(64,81,137,0.08)" }}>
                <CardBody className="p-4">
                  
                  {/* Form Header */}
                  <div className="d-flex align-items-center gap-3 mb-4 pb-3" style={{ borderBottom: "1.5px solid #eef0f7" }}>
                    <div>
                      <h5 className="mb-0 fw-bold text-dark">{id ? "Edit Purchase Order" : "New Purchase Order"}</h5>
                      <p className="mb-0 text-muted small">Record inventory arrivals and payment status</p>
                    </div>
                  </div>

                  {/* Section 1 — Basic Information */}
                  <div style={sectionStyle}>
                    <div style={sectionHeaderStyle}>
                      <i className="ri-information-line" style={{ color: "#405189" }}></i>
                      General Information
                    </div>
                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label className="fw-medium small text-dark">
                            Purchase Date <span className="text-danger">*</span>
                          </Label>
                          <Flatpickr
                            options={{ dateFormat: "Y-m-d" }}
                            value={formik.values.purchaseDate}
                            onChange={([date]) => formik.setFieldValue("purchaseDate", date)}
                            placeholder="Select Date"
                            className="form-control"
                            style={{ borderRadius: "8px", fontSize: "0.875rem" }}
                            onBlur={() => formik.setFieldTouched("purchaseDate", true)}
                          />
                          {formik.touched.purchaseDate && formik.errors.purchaseDate && (
                            <div className="invalid-feedback d-block small">{formik.errors.purchaseDate}</div>
                          )}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label className="fw-medium small text-dark">
                            Supplier <span className="text-danger">*</span>
                          </Label>
                          <Select
                            options={supplierOptions}
                            value={supplierOptions.find((o) => o.value === formik.values.supplierId) || null}
                            onChange={(opt) => formik.setFieldValue("supplierId", opt ? opt.value : "")}
                            placeholder="Select Supplier"
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                          />
                          {formik.touched.supplierId && formik.errors.supplierId && (
                            <div className="text-danger small mt-1">{formik.errors.supplierId}</div>
                          )}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label className="fw-medium small text-dark">
                            Voucher No. <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="voucherNo"
                            placeholder="Enter Voucher No."
                            style={{ borderRadius: "8px", fontSize: "0.875rem" }}
                            value={formik.values.voucherNo}
                            onChange={formik.handleChange}
                            invalid={!!(formik.touched.voucherNo && formik.errors.voucherNo)}
                          />
                          {formik.touched.voucherNo && formik.errors.voucherNo && (
                            <div className="invalid-feedback d-block small">{formik.errors.voucherNo}</div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Section 2 — Items Table */}
                  <div style={sectionStyle}>
                    <div style={sectionHeaderStyle} className="justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <i className="ri-list-check-2" style={{ color: "#0ab39c" }}></i>
                        Purchase Items
                      </div>
                      <Button
                        color="primary"
                        size="sm"
                        type="button"
                        onClick={handleAddItem}
                        style={{ borderRadius: "6px", fontSize: "0.7rem", padding: "2px 10px" }}
                      >
                        <i className="ri-add-line"></i> Add Item
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <Table className="align-middle mb-0" size="sm">
                        <thead className="text-muted table-light">
                          <tr style={{ fontSize: "0.75rem" }}>
                            <th style={{ width: "40px" }}>#</th>
                            <th>Product</th>
                            <th style={{ width: "100px" }}>Qty</th>
                            <th style={{ width: "120px" }}>Price</th>
                            <th className="text-end" style={{ width: "120px" }}>Total</th>
                            <th className="text-center" style={{ width: "50px" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formik.values.items.map((item, index) => (
                            <tr key={index}>
                              <td className="text-muted small">{index + 1}</td>
                              <td>
                                <Select
                                  options={productOptions}
                                  value={productOptions.find((o) => o.value === item.productId) || null}
                                  onChange={(opt) => handleProductChange(index, opt)}
                                  placeholder="Select..."
                                  styles={selectStyles}
                                  menuPortalTarget={document.body}
                                />
                              </td>
                              <td>
                                <Input
                                  name={`items[${index}].quantity`}
                                  type="number"
                                  bsSize="sm"
                                  className="text-center"
                                  style={{ borderRadius: "6px" }}
                                  value={item.quantity}
                                  onChange={formik.handleChange}
                                />
                              </td>
                              <td>
                                <Input
                                  name={`items[${index}].unitPrice`}
                                  type="number"
                                  bsSize="sm"
                                  style={{ borderRadius: "6px" }}
                                  value={item.unitPrice}
                                  onChange={formik.handleChange}
                                />
                              </td>
                              <td className="text-end fw-semibold text-dark">
                                <NumberWithCommas number={calculateTotal(item.quantity, item.unitPrice)} />
                              </td>
                              <td className="text-center">
                                <Button
                                  color="link"
                                  size="sm"
                                  className="text-danger p-0"
                                  onClick={() => handleRemoveItem(index)}
                                  disabled={formik.values.items.length === 1}
                                >
                                  <i className="ri-delete-bin-line fs-16"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  {/* Section 3 — Payment */}
                  <div style={{ ...sectionStyle, marginBottom: 0 }}>
                    <div style={sectionHeaderStyle}>
                      <i className="ri-bank-card-line" style={{ color: "#f7b84b" }}></i>
                      Payment Details
                    </div>
                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label className="fw-medium small text-dark">Payment Method</Label>
                          <Select
                            options={paymentTypeData.map((p) => ({ value: p.id, label: p.name }))}
                            value={paymentTypeData.map((p) => ({ value: p.id, label: p.name })).find((o) => o.value === formik.values.payment.paymentMethodId) || null}
                            onChange={(opt) => formik.setFieldValue("payment.paymentMethodId", opt ? opt.value : "")}
                            placeholder="Select..."
                            styles={selectStyles}
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label className="fw-medium small text-dark">Amount Paid</Label>
                          <Input
                            type="number"
                            name="payment.amountPaid"
                            style={{ borderRadius: "8px", fontSize: "0.875rem" }}
                            value={formik.values.payment.amountPaid}
                            onChange={formik.handleChange}
                            invalid={!!(formik.touched.payment?.amountPaid && formik.errors.payment?.amountPaid)}
                          />
                          {formik.touched.payment?.amountPaid && formik.errors.payment?.amountPaid && (
                            <div className="invalid-feedback d-block small">{formik.errors.payment.amountPaid}</div>
                          )}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label className="fw-medium small text-dark">Remarks</Label>
                          <Input
                            type="textarea"
                            name="payment.paymentRemarks"
                            value={formik.values.payment.paymentRemarks}
                            onChange={formik.handleChange}
                            placeholder="Add notes..."
                            rows={1}
                            style={{ borderRadius: "8px", fontSize: "0.875rem", resize: "none" }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>

                </CardBody>
              </Card>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                <Button color="light" type="button" onClick={handleCancel}
                  style={{ borderRadius: "8px", fontWeight: "500", fontSize: "0.875rem", minWidth: "100px" }}>
                  Discard
                </Button>
                <Button color="primary" type="submit" disabled={loading}
                  style={{
                    borderRadius: "8px", fontWeight: "500", fontSize: "0.875rem",
                    minWidth: "160px",
                    background: "linear-gradient(135deg, #405189 0%, #0ab39c 100%)",
                    border: "none",
                  }}>
                  {loading && <Spinner size="sm" className="me-2" />}
                  <i className="ri-check-line me-1"></i>
                  {id ? "Save Changes" : "Create Purchase"}
                </Button>
              </div>
            </Col>

            {/* ─── RIGHT: Summary (Mobile: Normal, Desktop: Sticky) ─── */}
            <Col lg={4} className="mt-4">
              <div className="sticky-md-top" style={{ top: "80px", zIndex: 10 }}>
                <div style={{
                  borderRadius: "14px",
                  overflow: "hidden",
                  boxShadow: "0 4px 24px rgba(64,81,137,0.13)",
                  border: "1px solid #eef0f7",
                }}>
                  {/* Header */}
                  <div style={{
                    background: "linear-gradient(135deg, #405189 0%, #0ab39c 100%)",
                    padding: "24px 20px 20px",
                    color: "#fff",
                  }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="ri-money-dollar-circle-line" style={{ fontSize: "18px", opacity: 0.85 }}></i>
                      <span style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.8 }}>
                        Purchase Summary
                      </span>
                    </div>
                    <div style={{ fontSize: "1.9rem", fontWeight: 700, letterSpacing: "-0.5px", lineHeight: 1.1 }}>
                      Rs.;<NumberWithCommas number={grandTotal} />
                    </div>
                    <div style={{ fontSize: "0.78rem", opacity: 0.75, marginTop: "4px" }}>
                      {formik.values.purchaseDate 
                        ? (formik.values.purchaseDate instanceof Date 
                            ? formik.values.purchaseDate.toLocaleDateString() 
                            : new Date(formik.values.purchaseDate).toLocaleDateString()) 
                        : "No date selected"}
                    </div>
                  </div>

                  {/* Summary Rows */}
                  <div style={{ background: "#fff" }}>
                    {[
                      {
                        icon: "ri-store-2-line", color: "#405189", label: "Supplier",
                        value: supplierOptions.find(o => o.value === formik.values.supplierId)?.label || "—",
                      },
                      {
                        icon: "ri-file-list-3-line", color: "#0ab39c", label: "Voucher No.",
                        value: formik.values.voucherNo || "—",
                      },
                      {
                        icon: "ri-shopping-cart-line", color: "#f7b84b", label: "Total Items",
                        value: formik.values.items.filter(i => i.productId).length + " / " + formik.values.items.length,
                      },
                      {
                        icon: "ri-bank-card-line", color: "#6c757d", label: "Method",
                        value: paymentTypeData.find(p => p.id === formik.values.payment.paymentMethodId)?.name || "—",
                      },
                      {
                        icon: "ri-exchange-dollar-line", color: "#6c757d", label: "Amount Paid",
                        value: "Rs. " + (parseFloat(formik.values.payment.amountPaid) || 0).toLocaleString(),
                      },
                    ].map((row, i, arr) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: "12px",
                        padding: "13px 18px",
                        borderBottom: i < arr.length - 1 ? "1px solid #f0f2f9" : "none",
                      }}>
                        <div style={{
                          width: "32px", height: "32px", borderRadius: "8px",
                          background: row.color + "18",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          <i className={row.icon} style={{ color: row.color, fontSize: "14px" }}></i>
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: "0.68rem", color: "#9aa1b9", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            {row.label}
                          </div>
                          <div style={{ fontSize: "0.84rem", fontWeight: 600, color: "#2e3650", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Remarks preview */}
                  {formik.values.payment.paymentRemarks && (
                    <div style={{ padding: "14px 18px", background: "#f8f9fc", borderTop: "1px solid #eef0f7" }}>
                      <div style={{ fontSize: "0.68rem", color: "#9aa1b9", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px" }}>
                        Remarks
                      </div>
                      <p style={{ fontSize: "0.82rem", color: "#5a6372", margin: 0, lineHeight: 1.5 }}>
                        {formik.values.payment.paymentRemarks}
                      </p>
                    </div>
                  )}
                </div>

                {/* Tips card */}
                <div style={{
                  marginTop: "16px", borderRadius: "12px", padding: "16px 18px",
                  background: "#fffbf0", border: "1px solid #fde8a0",
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="ri-lightbulb-line" style={{ color: "#f7b84b", fontSize: "16px" }}></i>
                    <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#7a5e00", textTransform: "uppercase", letterSpacing: "0.08em" }}>Purchase Tips</span>
                  </div>
                  <ul style={{ fontSize: "0.8rem", color: "#6b5500", margin: 0, paddingLeft: "16px", lineHeight: 1.9 }}>
                    <li>Ensure item quantities and unit prices are accurate.</li>
                    <li>Unselected rows won't be sent to the server.</li>
                    <li>Payment method is optional (Credit Purchase).</li>
                  </ul>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default PurchaseFormPage;

